import {Date as ApiDate} from 'gabi-api-ts/v2/common/date/date';
import {SignalType} from 'gabi-api-ts/v2/common/signals/signals';
import {PatientType} from 'gabi-api-ts/v2/patient/common/patient_common';
import {HasData} from 'gabi-api-ts/v2/signal/query/signal_query';
import {SignalQueryServiceClient} from 'gabi-api-ts/v2/signal/query/signal_query.client';
import {useTranslation} from 'react-i18next';
import {Bar, ComposedChart, Line, Tooltip, XAxis, YAxis} from 'recharts';
import {CategoricalChartFunc} from 'recharts/types/chart/generateCategoricalChart';
import styled from 'styled-components';

import {AverageValue} from '@/components/business/analytics/health-report/health-report-average-value';
import {ChartAxisTickColored} from '@/components/business/analytics/patient/patient-chart-axis/chart-axis-tick-colored';
import {ChartAxisTickDate} from '@/components/business/analytics/patient/patient-chart-axis/chart-axis-tick-date';
import {ChartAxisTickDuration} from '@/components/business/analytics/patient/patient-chart-axis/chart-axis-tick-duration';
import {getDomainForDuration, getDomainForEvents, getDomainForSignalType} from '@/components/business/analytics/timeline/timeline-chart-domains';
import {ChartContainer} from '@/components/layout/chart-container';
import LoadingView from '@/components/static/loading-view';
import ComponentErrorMessage from '@/components/widgets/component-error-message';
import {usePatientData} from '@/hooks/backend/use-patient-data';
import {useBackendQuery} from '@/hooks/use-backend-query';
import {SignalDefinitions} from '@/model/signal-definitions/signal-definition';
import {GlobalContext, PatientContextService} from '@/services/patient-context-service';
import {colorPalette} from '@/themes/darkmode';
import {getMax} from '@/util/array-util';
import {formatDateUniversal, formatSecondsToHms} from '@/util/date-utils';
import {getSignalTypeDualLabel, getSignalTypeUnit} from '@/util/signal-type-util';
import {roundDurationHoursToQuarterPrecision} from '@/util/time-utils';

type HealthReportStatisticsPerDayDualProps = {
    patientId: string,
    signalTypeMin: SignalType,
    signalTypeMax: SignalType,
    lastRecordingDate: ApiDate,
    dateFrom: ApiDate;
    dateTo: ApiDate;
    ignoreUnifiedDuration?: boolean,
    displayPreviousNightsAverage: boolean,
    onClick: (date: Date) => void,
}

type StatisticPerDayDualFormatted = {
    date: string;
    min: number | null;
    max: number | null;
    minmax: [ number | null, number | null ];
    hasEnoughData: boolean;
    reliableDurationInSeconds: number;
    duration: number | null;
    standardDeviation: [number, number] | null;
}

type HealthReportStatisticsPerDayTooltipProps = {
    payload?: Array<{
        payload?: StatisticPerDayDualFormatted;
    }>;
}

/*
  TODO this component would benefit a split in 3 :
  - the charting area
  - the statistics per day as displayed in the health report, ie this component with displayPreviousNightsAverage=false
  - the statistics per day as displayed in the patients list, ie this component with displayPreviousNightsAverage=true
  This would greatly improve the understability of this component, and avoid using the disabled flag of useBackendQuery
*/

const HealthReportStatisticsPerDayDual = ({patientId, signalTypeMin, signalTypeMax, dateFrom, dateTo, ignoreUnifiedDuration, onClick}: HealthReportStatisticsPerDayDualProps) => {
    const { t } = useTranslation();
    const signalTypeUnit = getSignalTypeUnit(signalTypeMin);

    const [patientDataLoading, patientData] = usePatientData({
        patientId: patientId,
        memoize: true,
    });

    const [unifiedDurationLoading, unifiedDuration] = useBackendQuery({
        serviceClient: SignalQueryServiceClient,
        query: SignalQueryServiceClient.prototype.getSignalsUnifiedDuration,
        memoize: true,
        data: {
            patientId: { id: patientId },
            signals: [SignalType.Signal_PR, SignalType.Signal_SPO2],
            from: dateFrom,
            to: dateTo,
        },
    });

    const [statisticsPerDayMinLoading, statisticsPerDayMin] = useBackendQuery({
        serviceClient: SignalQueryServiceClient,
        query: SignalQueryServiceClient.prototype.getStatisticsPerDay,
        memoize: true,
        data: {
            patientId: { id: patientId },
            signalType: signalTypeMin,
            from: dateFrom,
            to: dateTo,
        },
    });
    const [statisticsPerDayMaxLoading, statisticsPerDayMax] = useBackendQuery({
        serviceClient: SignalQueryServiceClient,
        query: SignalQueryServiceClient.prototype.getStatisticsPerDay,
        memoize: true,
        data: {
            patientId: { id: patientId },
            signalType: signalTypeMax,
            from: dateFrom,
            to: dateTo,
        },
    });

    const [signalAverageMinForPeriodLoading, signalAverageMinForPeriod] = useBackendQuery({
        serviceClient: SignalQueryServiceClient,
        query: SignalQueryServiceClient.prototype.getSignalAverageForPeriod,
        memoize: true,
        data: {
            patientId: { id: patientId },
            signalType: signalTypeMin,
            from: dateFrom,
            to: dateTo,
        },
    });
    const [signalAverageMaxForPeriodLoading, signalAverageMaxForPeriod] = useBackendQuery({
        serviceClient: SignalQueryServiceClient,
        query: SignalQueryServiceClient.prototype.getSignalAverageForPeriod,
        memoize: true,
        data: {
            patientId: { id: patientId },
            signalType: signalTypeMax,
            from: dateFrom,
            to: dateTo,
        },
    });

    const loading = (
        (patientDataLoading) ||
        (unifiedDurationLoading || !unifiedDuration) ||
        (statisticsPerDayMinLoading || !statisticsPerDayMin) ||
        (statisticsPerDayMaxLoading || !statisticsPerDayMax) ||
        // (signalsEventConfigurationLoading || !signalsEventConfiguration) ||
        // (signalAverageSummaryLoading) ||
        (signalAverageMinForPeriodLoading) ||
        (signalAverageMaxForPeriodLoading)
    );

    const isGabi = PatientContextService.getGlobalContext() === GlobalContext.GABI;

    const statisticsPerDayFormatted: StatisticPerDayDualFormatted[] = [];
    if (!statisticsPerDayMinLoading && statisticsPerDayMin && !statisticsPerDayMaxLoading && statisticsPerDayMax) {
        statisticsPerDayMin.statisticsPerDay.forEach((statisticMin, idx: number) => {
            const statisticMax = statisticsPerDayMax?.statisticsPerDay[idx];
            // TODO temporary - accept lower hasdata for parent
            const hasEnoughData = patientData!.patientType === PatientType.Adult ? (
                statisticMin.hasRecording && statisticMin.hasData >= HasData.HAS_RELIABLE_DATA
            ) : (
                statisticMin.hasRecording && statisticMin.hasData >= HasData.HAS_MIN_RELIABLE_DATA
            );

            let duration;
            if (isGabi) {
                duration = (statisticMin.hasRecording && statisticMin.reliableDurationInSeconds > 0) ? roundDurationHoursToQuarterPrecision(statisticMin.reliableDurationInSeconds) : null;
            }
            else {
                duration = statisticMin.reliableDurationInSeconds; // Actually number of samples
            }

            statisticsPerDayFormatted.push({
                date: statisticMin.date,
                hasEnoughData: hasEnoughData,
                minmax: [
                    hasEnoughData ? statisticMin.average : null,
                    hasEnoughData ? statisticMax.average : null,
                ],
                min: hasEnoughData ? statisticMin.average : null,
                max: hasEnoughData ? statisticMax.average : null,
                reliableDurationInSeconds: statisticMin.reliableDurationInSeconds,
                duration: duration,
                standardDeviation: ((hasEnoughData && statisticMin.reliableDurationInSeconds > 0) ? [
                    statisticMin.min,
                    statisticMin.max,
                ] : null)
            });
        });
    }

    const signalRange = (statisticsPerDayMinLoading || !statisticsPerDayMin) ? [0, 1000] : [statisticsPerDayMin.rangeMin, statisticsPerDayMin.rangeMax];

    const handleClick: CategoricalChartFunc = (rechartData) => {
        if (onClick) {
            if (rechartData && rechartData.activePayload && rechartData.activePayload[0]) {
                const rechartsPayload = rechartData?.activePayload[0].payload;
                if (rechartsPayload) {
                    const selectedDate = new Date(rechartsPayload.date);
                    const duration = rechartsPayload.reliableDurationInSeconds;
                    if (duration && duration > 0) {
                        onClick(selectedDate);
                    }
                }
            }
        }
    };

    const renderTooltip = (data: HealthReportStatisticsPerDayTooltipProps) => {
        if (data?.payload && data?.payload[0]?.payload) {
            const payload = data.payload[0].payload;
            const minmax = payload.minmax;
            const reliableDurationInSeconds = payload.reliableDurationInSeconds ?? 0;
            const hasEnoughData = payload.hasEnoughData;

            return (
                <div className="chart-tooltip">
                    <strong>{formatDateUniversal(new Date(payload.date + ' 00:00:00'))}</strong><br />
                    {reliableDurationInSeconds ? (
                        <span className="tooltip-duration">
                            {PatientContextService.getGlobalContext() === GlobalContext.GABI ?
                                `${t('healthReport.statistics.last7nights.duration')}: ${formatSecondsToHms(reliableDurationInSeconds)}` :
                                `Measurements: ${reliableDurationInSeconds}`
                            }
                        </span>
                    ) : (
                        <span>{t('healthReport.statistics.last7nights.noRecordingForThisDay')}</span>
                    )}
                    {!reliableDurationInSeconds ? (
                        <span>{t('healthReport.statistics.last7nights.noRecordingForThisDay')}</span>
                    ) : (!hasEnoughData ? (
                        <span>{t('healthReport.statistics.last7nights.notEnoughDataForThisDay')}</span>
                    ) : (
                        <span className="tooltip-average">
                            {t('healthReport.statistics.last7nights.average')}: {Math.round((minmax!)[0]!)} - {Math.round((minmax!)[1]!)}{signalTypeUnit}
                        </span>
                    ))}
                </div>
            );
        }
        return null;
    };

    const domainForSignalType = getDomainForSignalType(signalTypeMin, signalRange[0], signalRange[1]);

    if (patientId && dateFrom && dateTo) {
        const title = getSignalTypeDualLabel(signalTypeMin);

        const topRightContent = (
            <div className="average-block">
                <p>
                    {t('healthReport.statistics.averageForPeriod')}{' '}
                    <AverageValue averageObj={signalAverageMinForPeriod ?? null} unit={signalTypeUnit} precision={0} />
                    {' - '}
                    <AverageValue averageObj={signalAverageMaxForPeriod ?? null} unit={signalTypeUnit} precision={0} />
                </p>
            </div>
        );

        const tooltipText = <>
            <p>
                {signalTypeMin === SignalType.Signal_PR &&
                    <>{t('infoButton.average.pulseRate')}&nbsp;</>
                }
                {signalTypeMin === SignalType.Signal_SPO2 &&
                    <>{t('infoButton.average.spo2')}&nbsp;</>
                }
                {t('infoButton.average.reliableData')}
            </p>
            <p>
                {t('infoButton.average.duration')}
            </p>
        </>;

        const signalColors = (SignalDefinitions[signalTypeMin] ?? SignalDefinitions[-1]).colors;

        if (!loading) {
            const maxDuration = ignoreUnifiedDuration ? getMax(statisticsPerDayFormatted, (point) => point.reliableDurationInSeconds).reliableDurationInSeconds : unifiedDuration?.maxUnifiedDurationInSeconds;
            const durationDomain = isGabi ? getDomainForDuration(maxDuration!) : getDomainForEvents(maxDuration, false);

            return (
                <StyledWrapper className="health-report-statistics-per-day" color={signalColors.light}>
                    <ChartContainer
                        title={title}
                        topRightContent={topRightContent}
                        infoTooltipTitle={`${title} - ${t('infoButton.average.title')}`}
                        infoTooltipText={tooltipText}
                        infoTooltipOverlayPosition="bottomLeft"
                    >
                        {(statisticsPerDayFormatted.length <= 0) ? (
                            <div className="empty-message">{t('healthReport.statistics.last7nights.empty')}</div>
                        ) : (
                            <ComposedChart data={statisticsPerDayFormatted} margin={{ top: 0, right: 0, bottom: 0, left: 10 }} onClick={handleClick}>
                                <XAxis
                                    dataKey="date"
                                    interval="equidistantPreserveStart"
                                    tick={<ChartAxisTickDate textAnchor="middle" />}
                                />
                                <YAxis
                                    yAxisId="left"
                                    orientation="left"
                                    dataKey="average"
                                    tick={(props) => <ChartAxisTickColored {...props} unit={signalTypeUnit} fill={signalColors.light} />}
                                    tickCount={4}
                                    domain={domainForSignalType.domain}
                                    ticks={domainForSignalType.ticks}
                                    interval={0}
                                    padding={{ top: 10 }}
                                    width={35}
                                />
                                {isGabi ? (
                                    <YAxis
                                        yAxisId="right"
                                        dataKey="duration"
                                        orientation="right"
                                        tick={<ChartAxisTickDuration textAnchor="start"/>}
                                        padding={{ top: 10 }}
                                        width={37}
                                        domain={durationDomain.domain}
                                        ticks={durationDomain.ticks}
                                        interval={0}
                                    />
                                ) : (
                                    <YAxis
                                        yAxisId="right"
                                        dataKey="duration"
                                        orientation="right"
                                        padding={{ top: 10 }}
                                        width={25}
                                        domain={durationDomain.domain}
                                        ticks={durationDomain.ticks}
                                    />
                                )}

                                <Bar
                                    dataKey="duration"
                                    fill={colorPalette.thirdBackground}
                                    yAxisId="right"
                                    barSize={40}
                                />

                                {/*<Area*/}
                                {/*    type="monotone"*/}
                                {/*    dot={false}*/}
                                {/*    activeDot={false}*/}
                                {/*    dataKey="minmax"*/}
                                {/*    fill={signalColors.normal}*/}
                                {/*    fillOpacity="0.3"*/}
                                {/*    strokeWidth={0}*/}
                                {/*    yAxisId="left"*/}
                                {/*/>*/}

                                <Line
                                    type="monotone"
                                    dot={{ fill: signalColors.normal }}
                                    dataKey="min"
                                    stroke={signalColors.normal}
                                    strokeWidth={2}
                                    isAnimationActive={false}
                                    yAxisId="left"
                                />
                                <Line
                                    type="monotone"
                                    dot={{ fill: signalColors.normal }}
                                    dataKey="max"
                                    stroke={signalColors.normal}
                                    strokeWidth={2}
                                    isAnimationActive={false}
                                    yAxisId="left"
                                />

                                <Tooltip
                                    content={renderTooltip}
                                    isAnimationActive={false}
                                />
                            </ComposedChart>
                        )}
                    </ChartContainer>
                </StyledWrapper>
            );
        }
        else {
            return (
                <StyledWrapper className="health-report-statistics-per-day" color={signalColors.light}>
                    <ChartContainer title={title}>
                        <LoadingView color={signalColors.normal} />
                    </ChartContainer>
                </StyledWrapper>
            );
        }
    }
    else {
        return <ComponentErrorMessage component="HealthReportStatisticsPerDay" />;
    }
};

//language=SCSS
const StyledWrapper = styled.div`
& {
    min-height: 260px;
    height: 100%;
    display: flex;
    flex-direction: column;
    
    > * {
        flex-grow: 1;
    }

    svg {
        cursor: pointer;
    }
    
    .tooltip-average {
        color: ${props => props.color};
    }
    
    .tooltip-duration {
        color: ${colorPalette.clearColor};
    }
    
    .average-block {
        p span {
            color: ${props => props.color};
        }
        img.warningFlag {
            width: 15px;
            margin-right: 7px;
            margin-top: -3px;
        }
    }
}
`;

export {HealthReportStatisticsPerDayDual};
